import React from 'react';
import { Typography } from 'antd';
import { BaseCard } from './BaseCard';
import { ReturnsByDueDateCardProps } from './CardProps';
import { ScopeTable } from 'components/Custom/table';
import { ReturnsPopover } from 'components/Custom/ReturnsPopover';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export const ReturnsByDueDateCard: React.FC<ReturnsByDueDateCardProps> = ({
  cardId,
  overdueReturns,
  dueTodayReturns,
  dueThisWeekReturns,
  dueThisMonthReturns,
  dueNextMonthReturns,
  dueLaterReturns,
  handleDueDateClick,
  getReturnsByDueDate,
  createSubmission,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <BaseCard
      cardId={cardId}
      bodyStyle={{
        paddingTop: '22px',
        paddingBottom: '24px',
        paddingLeft: '24px',
        paddingRight: '24px',
      }}
    >
      <div>
        <Text
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            display: 'block',
            marginBottom: '16px',
            lineHeight: '16px',
          }}
        >
          {t('dashboard.returns-by-due-date-description')}
        </Text>
        <style>{`
          .returns-by-due-date-table .ant-table-thead {
            display: none !important;
          }
          .returns-by-due-date-table .ant-table-title {
            display: none !important;
          }
          .returns-by-due-date-table .ant-table-container {
            margin-top: 0 !important;
          }
          .returns-by-due-date-table .ant-table-body {
            margin-top: 0 !important;
          }
        `}</style>
        <ScopeTable
          className="returns-by-due-date-table"
          tableDataSource={[
          {
            key: 'overdue',
            label: t('billing.overdue'),
            count: overdueReturns,
            color: '#FF4D4F',
          },
          {
            key: 'dueToday',
            label: t('dashboard.due-today'),
            count: dueTodayReturns,
            color: '#FFA940',
          },
          {
            key: 'dueThisWeek',
            label: t('dashboard.due-this-week'),
            count: dueThisWeekReturns,
            color: '#73D13D',
          },
          {
            key: 'dueThisMonth',
            label: t('dashboard.due-this-month'),
            count: dueThisMonthReturns,
            color: '#40A9FF',
          },
          {
            key: 'dueNextMonth',
            label: t('dashboard.due-next-month'),
            count: dueNextMonthReturns,
            color: '#69C0FF',
          },
          {
            key: 'dueLater',
            label: t('dashboard.due-later'),
            count: dueLaterReturns,
            color: '#D9D9D9',
          },
        ]}
        columns={[
          {
            title: '',
            dataIndex: 'label',
            key: 'label',
            render: (text: string, record: any) => {
              const returns = getReturnsByDueDate(record.key);
              const cellContent = (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: record.color,
                      borderRadius: '2px',
                      marginRight: '12px',
                    }}
                  />
                  <Text
                    style={{
                      fontSize: '14px',
                      color: 'var(--color-text-dark, #042a2a)',
                      fontWeight: 500,
                    }}
                  >
                    {text}
                  </Text>
                </div>
              );
              return record.count > 0 && returns.length > 0 ? (
                <ReturnsPopover
                  returns={returns}
                  onClick={() => handleDueDateClick(record.key)}
                  onCreateSubmission={createSubmission}
                >
                  {cellContent}
                </ReturnsPopover>
              ) : (
                cellContent
              );
            },
          },
          {
            title: '',
            dataIndex: 'count',
            key: 'count',
            align: 'right' as const,
            render: (count: number, record: any) => {
              const returns = getReturnsByDueDate(record.key);
              const cellContent = (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Text
                    style={{
                      fontSize: '14px',
                      color: 'var(--color-text-dark, #042a2a)',
                      fontWeight: 600,
                      cursor: count > 0 ? 'pointer' : 'default',
                    }}
                    onClick={
                      count > 0
                        ? () => handleDueDateClick(record.key)
                        : undefined
                    }
                  >
                    {formatDisplayInteger(count, i18n.language)}
                  </Text>
                </div>
              );
              return record.count > 0 && returns.length > 0 ? (
                <ReturnsPopover
                  returns={returns}
                  onClick={() => handleDueDateClick(record.key)}
                  onCreateSubmission={createSubmission}
                >
                  {cellContent}
                </ReturnsPopover>
              ) : (
                cellContent
              );
            },
          },
        ]}
        pagination={false}
        rowsPerPage={5}
        enableColumnsCustomization={false}
        rowHeight={40}
        fontSize={12}
        showSizeChanger={false}
        getNewPageSize={() => {}}
        transactionType=""
        reportId=""
        onRow={(record: any) => ({
          style: { cursor: record.count > 0 ? 'pointer' : 'default' },
          onClick:
            record.count > 0
              ? () => handleDueDateClick(record.key)
              : undefined,
        })}
      />
      </div>
    </BaseCard>
  );
};

